import type { Job } from './types';

/* Open roles. Authored in English and translated per locale; ‹…› marks orange accent
   and is stripped for <title>/og. Adding a role here is enough: the index, the detail
   page, the sitemap and the interview booking all read from this list.

   Keep the copy concrete. Named tools, real problems and the honest downsides are what
   separate a posting from a template; claims about impact and innovation are not. */

export const AI_ENGINEER_SLUG = 'ai-engineer';
export const FULL_STACK_SLUG = 'full-stack-developer';

export const JOBS: Job[] = [
  {
    slug: AI_ENGINEER_SLUG,
    postedAt: '2026-09-05',
    i18n: {
      en: {
        title: 'AI ‹Engineer›',
        excerpt:
          'Own the agents that talk to homeowners, and the pipeline that turns an address into a valuation an agent can defend.',
        team: 'Engineering',
        location: 'Belgium',
        workplace: 'On-site',
        employment: 'Full-time',
        body: [
          { t: 'lede', v: 'Estate agency runs on conversations: the first message from an owner thinking about selling, the follow-up a week later, the reminder nobody sent. You would build the agents that handle them, in a small team at WAT in Brussels.' },
          { t: 'h2', v: 'About Cultiv' },
          { t: 'p', v: 'We started with an AI assistant that generated seller leads on estate agency websites. It worked, but agencies kept telling us the hard part starts after the lead arrives: follow-up, valuations, reports, the compromis. So we built the rest of it. Today more than 100 agencies in Belgium and France work with us, and the CRM is open from the moment they arrive until they close.' },
          { t: 'h2', v: 'The role' },
          { t: 'p', v: 'You own the AI layer: the widget conversation, the valuation pipeline behind it, and the follow-up that runs without anyone pressing a button. It runs on Claude and Azure OpenAI today, with automations in n8n, and you decide where it goes next. Expect to spend as much time on evaluation, and on what happens when a model is confidently wrong, as on the prompts themselves.' },
          { t: 'h2', v: 'What you’ll do' },
          {
            t: 'list',
            v: [
              'Run the widget conversation. It has to qualify a seller in a dozen messages, in Dutch, French or English, without feeling like a form.',
              'Own the valuation pipeline. Three independent methods (comparable sales, land plus construction, rental yield) each produce a number, and something has to reconcile them into one estimate with a range an agent can defend to a client.',
              'Write the tools the agents call: cadastre lookups, comparable sales, EPC and permit documents, the CRM itself.',
              'Build the evaluation harness, so that whether a prompt change helped is measured instead of read from transcripts.',
              'Watch cost and latency. A homeowner abandons a chat that thinks for eight seconds.',
            ],
          },
          { t: 'h2', v: 'Your profile' },
          {
            t: 'group',
            label: '📈 Experience & background',
            v: [
              'At least a year of software engineering, most of it on AI products.',
              'You have shipped an LLM system to real users and stayed responsible for it afterwards.',
              'A degree in computer science or engineering, or projects that show the same thing.',
              'Dutch native speaker.',
            ],
          },
          {
            t: 'group',
            label: '💻 Technical skills',
            v: [
              'Python for the AI services, and enough TypeScript to work inside a Next.js codebase.',
              'Hands-on with the Claude, OpenAI or Gemini APIs: tool use, function calling, structured outputs.',
              'Experience with multi-agent orchestration, and a sense of when a single well-built prompt is enough.',
              'You know how to evaluate an LLM system beyond reading transcripts.',
              'RAG where it is useful. Much of what we handle is structured data rather than documents.',
            ],
          },
          {
            t: 'group',
            label: '🧠 Mindset',
            v: [
              'You put something unfinished in front of a user in order to learn from it.',
              'You take responsibility for what you build once it is live.',
              'You can tell a model that demos well from one an agency relies on daily.',
              'You follow model releases and can judge which ones matter for us.',
              'You want to understand how estate agents actually work, and you ask them.',
            ],
          },
          { t: 'h2', v: 'What we offer' },
          {
            t: 'list',
            v: [
              'A competitive salary package.',
              'A small team. What you ship is in front of agencies within days.',
              'A desk at [WAT](https://www.linkedin.com/company/wat-bxl), the best incubator in Brussels, alongside other early-stage tech companies.',
              'Full ownership of the AI layer. There is no ML team above you.',
              'Real data problems: cadastre records, EPC certificates, listings that contradict each other.',
            ],
          },
          { t: 'h2', v: 'Our process' },
          {
            t: 'list',
            v: [
              'A first interview online with Cultiv’s CTO (30 min).',
              'The technical interview at our office.',
              'You meet the rest of the team over lunch.',
            ],
          },
        ],
      },
      nl: {
        title: 'AI ‹Engineer›',
        excerpt:
          'Bouw de agents die met eigenaars praten, en de pipeline die van een adres een schatting maakt die een makelaar kan verdedigen.',
        team: 'Engineering',
        location: 'België',
        workplace: 'Op kantoor',
        employment: 'Voltijds',
        body: [
          { t: 'lede', v: 'Vastgoed draait op gesprekken: het eerste bericht van een eigenaar die aan verkopen denkt, de opvolging een week later, de herinnering die niemand verstuurde. Jij bouwt de agents die dat overnemen, in een klein team bij WAT in Brussel.' },
          { t: 'h2', v: 'Over Cultiv' },
          { t: 'p', v: 'We begonnen met een AI-assistent die verkopersleads genereerde op de websites van vastgoedkantoren. Het werkte, maar kantoren bleven ons zeggen dat het moeilijke deel pas begint als de lead binnen is: opvolging, schattingen, verslagen, het compromis. Dus bouwden we de rest. Vandaag werken meer dan 100 kantoren in België en Frankrijk met ons, en het CRM staat open van het moment dat ze toekomen tot ze sluiten.' },
          { t: 'h2', v: 'De rol' },
          { t: 'p', v: 'Jij bent eigenaar van de AI-laag: het widgetgesprek, de schattingspipeline erachter, en de opvolging die draait zonder dat iemand op een knop duwt. Vandaag draait die op Claude en Azure OpenAI, met automatiseringen in n8n, en jij beslist waar het naartoe gaat. Reken erop dat je evenveel tijd steekt in evaluatie, en in wat er gebeurt als een model zelfzeker fout zit, als in de prompts zelf.' },
          { t: 'h2', v: 'Wat je doet' },
          {
            t: 'list',
            v: [
              'Het widgetgesprek draaiende houden. Het moet een verkoper in een tiental berichten kwalificeren, in het Nederlands, Frans of Engels, zonder als een formulier aan te voelen.',
              'De schattingspipeline dragen. Drie onafhankelijke methodes (vergelijkbare verkopen, grond plus bouwkost, huurrendement) geven elk een cijfer, en iets moet die verzoenen tot één schatting met een vork die een makelaar aan een klant kan uitleggen.',
              'De tools schrijven die de agents aanroepen: kadasteropzoekingen, vergelijkbare verkopen, EPC- en vergunningsdocumenten, het CRM zelf.',
              'Het evaluatieharnas bouwen, zodat we meten of een promptwijziging hielp in plaats van transcripten te lezen.',
              'Kost en latency in het oog houden. Een eigenaar haakt af bij een chat die acht seconden nadenkt.',
            ],
          },
          { t: 'h2', v: 'Jouw profiel' },
          {
            t: 'group',
            label: '📈 Ervaring & achtergrond',
            v: [
              'Minstens een jaar software engineering, grotendeels aan AI-producten.',
              'Je hebt een LLM-systeem bij echte gebruikers gekregen en bent er nadien verantwoordelijk voor gebleven.',
              'Een diploma informatica of ingenieur, of projecten die hetzelfde aantonen.',
              'Moedertaal Nederlands.',
            ],
          },
          {
            t: 'group',
            label: '💻 Technische vaardigheden',
            v: [
              'Python voor de AI-services, en genoeg TypeScript om in een Next.js-codebase te werken.',
              'Praktijkervaring met de API’s van Claude, OpenAI of Gemini: tool use, function calling, structured outputs.',
              'Ervaring met multi-agentorkestratie, en gevoel voor wanneer één goed gebouwde prompt volstaat.',
              'Je weet hoe je een LLM-systeem evalueert, verder dan transcripten lezen.',
              'RAG waar het nuttig is. Veel van wat wij verwerken is gestructureerde data, geen documenten.',
            ],
          },
          {
            t: 'group',
            label: '🧠 Mentaliteit',
            v: [
              'Je legt iets onafgewerkts voor aan een gebruiker om ervan te leren.',
              'Je neemt verantwoordelijkheid voor wat je bouwt zodra het live staat.',
              'Je ziet het verschil tussen een model dat goed demonstreert en een model waar een kantoor dagelijks op rekent.',
              'Je volgt de releases van modellen en kan inschatten welke voor ons tellen.',
              'Je wil begrijpen hoe makelaars echt werken, en je vraagt het hen.',
            ],
          },
          { t: 'h2', v: 'Wat wij bieden' },
          {
            t: 'list',
            v: [
              'Een competitief salarispakket.',
              'Een klein team. Wat je oplevert staat binnen enkele dagen bij kantoren.',
              'Een plek bij [WAT](https://www.linkedin.com/company/wat-bxl), de beste incubator van Brussel, tussen andere jonge techbedrijven.',
              'Volledig eigenaarschap van de AI-laag. Er zit geen ML-team boven je.',
              'Echte dataproblemen: kadastergegevens, EPC-attesten, advertenties die elkaar tegenspreken.',
            ],
          },
          { t: 'h2', v: 'Ons proces' },
          {
            t: 'list',
            v: [
              'Een eerste gesprek online met de CTO van Cultiv (30 min).',
              'Het technische gesprek op ons kantoor.',
              'Je leert de rest van het team kennen tijdens de lunch.',
            ],
          },
        ],
      },
      fr: {
        title: 'AI ‹Engineer›',
        excerpt:
          'Construisez les agents qui parlent aux propriétaires, et le pipeline qui transforme une adresse en une estimation qu’un agent peut défendre.',
        team: 'Engineering',
        location: 'Belgique',
        workplace: 'Sur site',
        employment: 'Temps plein',
        body: [
          { t: 'lede', v: 'L’immobilier tourne sur des conversations : le premier message d’un propriétaire qui songe à vendre, la relance une semaine plus tard, le rappel que personne n’a envoyé. Vous construisez les agents qui s’en chargent, dans une petite équipe chez WAT à Bruxelles.' },
          { t: 'h2', v: 'À propos de Cultiv' },
          { t: 'p', v: 'Nous avons commencé avec un assistant IA qui générait des leads vendeurs sur les sites d’agences immobilières. Ça marchait, mais les agences nous répétaient que le plus dur commence une fois le lead arrivé : relance, estimations, rapports, compromis. Alors nous avons construit le reste. Aujourd’hui, plus de 100 agences en Belgique et en France travaillent avec nous, et le CRM reste ouvert de leur arrivée jusqu’à la fermeture.' },
          { t: 'h2', v: 'Le poste' },
          { t: 'p', v: 'Vous êtes propriétaire de la couche IA : la conversation du widget, le pipeline d’estimation derrière, et les relances qui partent sans que personne n’appuie sur un bouton. Elle tourne aujourd’hui sur Claude et Azure OpenAI, avec des automatisations dans n8n, et vous décidez de la suite. Attendez-vous à passer autant de temps sur l’évaluation, et sur ce qui se produit quand un modèle se trompe avec assurance, que sur les prompts eux-mêmes.' },
          { t: 'h2', v: 'Vos missions' },
          {
            t: 'list',
            v: [
              'Faire tourner la conversation du widget. Elle doit qualifier un vendeur en une dizaine de messages, en néerlandais, français ou anglais, sans donner l’impression d’un formulaire.',
              'Porter le pipeline d’estimation. Trois méthodes indépendantes (ventes comparables, terrain plus construction, rendement locatif) donnent chacune un chiffre, et quelque chose doit les réconcilier en une estimation assortie d’une fourchette qu’un agent peut justifier devant un client.',
              'Écrire les outils que les agents appellent : recherches cadastrales, ventes comparables, PEB et permis, le CRM lui-même.',
              'Construire le harnais d’évaluation, pour mesurer si un changement de prompt a aidé au lieu de lire des transcriptions.',
              'Surveiller le coût et la latence. Un propriétaire abandonne une conversation qui réfléchit huit secondes.',
            ],
          },
          { t: 'h2', v: 'Votre profil' },
          {
            t: 'group',
            label: '📈 Expérience & parcours',
            v: [
              'Au moins un an d’ingénierie logicielle, en grande partie sur des produits IA.',
              'Vous avez mis un système LLM entre les mains de vrais utilisateurs et vous en êtes resté responsable ensuite.',
              'Un diplôme en informatique ou en ingénierie, ou des projets qui le démontrent.',
              'Néerlandais langue maternelle.',
            ],
          },
          {
            t: 'group',
            label: '💻 Compétences techniques',
            v: [
              'Python pour les services IA, et assez de TypeScript pour travailler dans une base de code Next.js.',
              'Une pratique des API Claude, OpenAI ou Gemini : tool use, function calling, sorties structurées.',
              'Une expérience de l’orchestration multi-agents, et le sens des cas où un seul prompt bien construit suffit.',
              'Vous savez évaluer un système LLM autrement qu’en lisant des transcriptions.',
              'Du RAG là où c’est utile. Une grande part de ce que nous traitons est de la donnée structurée, pas des documents.',
            ],
          },
          {
            t: 'group',
            label: '🧠 État d’esprit',
            v: [
              'Vous montrez quelque chose d’inachevé à un utilisateur pour en apprendre.',
              'Vous assumez ce que vous construisez une fois en ligne.',
              'Vous distinguez un modèle qui fait une belle démo d’un modèle sur lequel une agence compte chaque jour.',
              'Vous suivez les sorties de modèles et jugez lesquelles comptent pour nous.',
              'Vous voulez comprendre comment les agents travaillent vraiment, et vous le leur demandez.',
            ],
          },
          { t: 'h2', v: 'Ce que nous offrons' },
          {
            t: 'list',
            v: [
              'Un package salarial compétitif.',
              'Une petite équipe. Ce que vous livrez est chez les agences en quelques jours.',
              'Un bureau chez [WAT](https://www.linkedin.com/company/wat-bxl), le meilleur incubateur de Bruxelles, aux côtés d’autres jeunes entreprises tech.',
              'La pleine propriété de la couche IA. Il n’y a pas d’équipe ML au-dessus de vous.',
              'De vrais problèmes de données : relevés cadastraux, certificats PEB, annonces qui se contredisent.',
            ],
          },
          { t: 'h2', v: 'Notre processus' },
          {
            t: 'list',
            v: [
              'Un premier entretien en ligne avec le CTO de Cultiv (30 min).',
              'L’entretien technique dans nos bureaux.',
              'Vous rencontrez le reste de l’équipe autour d’un déjeuner.',
            ],
          },
        ],
      },
    },
  },
  {
    slug: FULL_STACK_SLUG,
    postedAt: '2026-09-05',
    i18n: {
      en: {
        title: 'Full-Stack ‹Developer›',
        excerpt:
          'Build the screens estate agents keep open all day: the pipeline board, the valuation wizard, the report generator.',
        team: 'Engineering',
        location: 'Belgium',
        workplace: 'On-site',
        employment: 'Full-time',
        body: [
          { t: 'lede', v: 'A mature product with real users, a small team, and an office at WAT in Brussels. You would own the screens agents work in all day.' },
          { t: 'h2', v: 'About Cultiv' },
          { t: 'p', v: 'We started with an AI assistant that generated seller leads on estate agency websites. It worked, but agencies kept telling us the hard part starts after the lead arrives: follow-up, valuations, reports, the compromis. So we built the rest of it. Today more than 100 agencies in Belgium and France work with us, and the CRM is open from the moment they arrive until they close.' },
          { t: 'h2', v: 'The role' },
          { t: 'p', v: 'You build the surfaces agents work in: the pipeline board, the valuation wizard, the report generator, and the inbox that brings email, WhatsApp and calls into one thread. You own each feature from the interface to the API and the data model, and you keep owning it after it goes live. Much of the platform already exists, so you will build on it as often as you add to it.' },
          { t: 'h2', v: 'What you’ll do' },
          {
            t: 'list',
            v: [
              'Ship features end to end: interface, API route, data model, and the migration that comes with them.',
              'Work on the report generator, which turns a template into a branded PDF valuation report per agency. It is the part of the product our clients hand to their own customers.',
              'Keep the app fast. It stays open all day, and a pipeline board carries thousands of leads per agency.',
              'Maintain and improve code you did not write.',
            ],
          },
          { t: 'h2', v: 'Your profile' },
          {
            t: 'group',
            label: '📈 Experience & background',
            v: [
              'At least a year of experience shipping software to real users.',
              'You have followed a feature through after launch: bugs, feedback, and the next iteration.',
              'A degree in computer science or engineering, or projects that show the same thing.',
              'Dutch native speaker.',
            ],
          },
          {
            t: 'group',
            label: '💻 Technical skills',
            v: [
              'TypeScript and React. Experience with the Next.js App Router and server components is a strong plus.',
              'Comfortable with a NoSQL database and with running something on AWS.',
              'An eye for interface detail. Much of this product is dense tables and forms that people use for hours.',
              'Experience with LLM APIs is a plus. There is as much AI work available as you want.',
            ],
          },
          {
            t: 'group',
            label: '🧠 Mindset',
            v: [
              'You prefer shipping something small and improving it over waiting for it to be perfect.',
              'You take responsibility for what you build once it is live.',
              'You check what the codebase already does before adding to it.',
              'You want to understand how estate agents actually work, and you ask them.',
            ],
          },
          { t: 'h2', v: 'What we offer' },
          {
            t: 'list',
            v: [
              'A competitive salary package.',
              'A small team. Your work is in front of agencies within days, and you decide how it looks and behaves.',
              'A desk at [WAT](https://www.linkedin.com/company/wat-bxl), the best incubator in Brussels, alongside other early-stage tech companies.',
              'A product in daily use, with clients who tell you what they need.',
            ],
          },
          { t: 'h2', v: 'Our process' },
          {
            t: 'list',
            v: [
              'A first interview online with Cultiv’s CTO (30 min).',
              'The technical interview at our office.',
              'You meet the rest of the team over lunch.',
            ],
          },
        ],
      },
      nl: {
        title: 'Full-Stack ‹Developer›',
        excerpt:
          'Bouw de schermen die makelaars de hele dag openhouden: het pipelinebord, de schattingswizard, de verslaggenerator.',
        team: 'Engineering',
        location: 'België',
        workplace: 'Op kantoor',
        employment: 'Voltijds',
        body: [
          { t: 'lede', v: 'Een matuur product met echte gebruikers, een klein team en een kantoor bij WAT in Brussel. Jij wordt eigenaar van de schermen waar makelaars de hele dag in werken.' },
          { t: 'h2', v: 'Over Cultiv' },
          { t: 'p', v: 'We begonnen met een AI-assistent die verkopersleads genereerde op de websites van vastgoedkantoren. Het werkte, maar kantoren bleven ons zeggen dat het moeilijke deel pas begint als de lead binnen is: opvolging, schattingen, verslagen, het compromis. Dus bouwden we de rest. Vandaag werken meer dan 100 kantoren in België en Frankrijk met ons, en het CRM staat open van het moment dat ze toekomen tot ze sluiten.' },
          { t: 'h2', v: 'De rol' },
          { t: 'p', v: 'Jij bouwt de schermen waarin makelaars werken: het pipelinebord, de schattingswizard, de verslaggenerator en de inbox die e-mail, WhatsApp en oproepen in één draad samenbrengt. Je draagt elke feature van interface tot API en datamodel, en je blijft ze dragen nadat ze live is. Een groot deel van het platform bestaat al, dus je bouwt er even vaak op verder als dat je iets nieuws toevoegt.' },
          { t: 'h2', v: 'Wat je doet' },
          {
            t: 'list',
            v: [
              'Features van begin tot eind opleveren: interface, API-route, datamodel, en de migratie die erbij hoort.',
              'Werken aan de verslaggenerator, die van een template een schattingsverslag in PDF maakt in de huisstijl van elk kantoor. Het is het stuk van het product dat onze klanten aan hún klanten geven.',
              'De app snel houden. Ze staat de hele dag open, en een pipelinebord draagt duizenden leads per kantoor.',
              'Code onderhouden en verbeteren die je niet zelf geschreven hebt.',
            ],
          },
          { t: 'h2', v: 'Jouw profiel' },
          {
            t: 'group',
            label: '📈 Ervaring & achtergrond',
            v: [
              'Minstens een jaar ervaring met software opleveren aan echte gebruikers.',
              'Je hebt een feature opgevolgd na de lancering: bugs, feedback en de volgende iteratie.',
              'Een diploma informatica of ingenieur, of projecten die hetzelfde aantonen.',
              'Moedertaal Nederlands.',
            ],
          },
          {
            t: 'group',
            label: '💻 Technische vaardigheden',
            v: [
              'TypeScript en React. Ervaring met de Next.js App Router en server components is een sterke plus.',
              'Vertrouwd met een NoSQL-database en met iets draaiende houden op AWS.',
              'Oog voor interfacedetail. Een groot deel van dit product zijn dichte tabellen en formulieren die mensen urenlang gebruiken.',
              'Ervaring met LLM-API’s is een plus. Er is zoveel AI-werk beschikbaar als je zelf wil.',
            ],
          },
          {
            t: 'group',
            label: '🧠 Mentaliteit',
            v: [
              'Je levert liever iets kleins op en verbetert het daarna, dan dat je wacht tot het perfect is.',
              'Je neemt verantwoordelijkheid voor wat je bouwt zodra het live staat.',
              'Je kijkt wat de codebase al doet vóór je er iets aan toevoegt.',
              'Je wil begrijpen hoe makelaars echt werken, en je vraagt het hen.',
            ],
          },
          { t: 'h2', v: 'Wat wij bieden' },
          {
            t: 'list',
            v: [
              'Een competitief salarispakket.',
              'Een klein team. Wat je oplevert staat binnen enkele dagen bij kantoren, en jij beslist hoe het eruitziet en aanvoelt.',
              'Een plek bij [WAT](https://www.linkedin.com/company/wat-bxl), de beste incubator van Brussel, tussen andere jonge techbedrijven.',
              'Een product dat dagelijks gebruikt wordt, met klanten die je vertellen wat ze nodig hebben.',
            ],
          },
          { t: 'h2', v: 'Ons proces' },
          {
            t: 'list',
            v: [
              'Een eerste gesprek online met de CTO van Cultiv (30 min).',
              'Het technische gesprek op ons kantoor.',
              'Je leert de rest van het team kennen tijdens de lunch.',
            ],
          },
        ],
      },
      fr: {
        title: 'Full-Stack ‹Developer›',
        excerpt:
          'Construisez les écrans que les agents gardent ouverts toute la journée : le pipeline, l’assistant d’estimation, le générateur de rapports.',
        team: 'Engineering',
        location: 'Belgique',
        workplace: 'Sur site',
        employment: 'Temps plein',
        body: [
          { t: 'lede', v: 'Un produit mature avec de vrais utilisateurs, une petite équipe et un bureau chez WAT à Bruxelles. Vous serez responsable des écrans dans lesquels les agents travaillent toute la journée.' },
          { t: 'h2', v: 'À propos de Cultiv' },
          { t: 'p', v: 'Nous avons commencé avec un assistant IA qui générait des leads vendeurs sur les sites d’agences immobilières. Ça marchait, mais les agences nous répétaient que le plus dur commence une fois le lead arrivé : relance, estimations, rapports, compromis. Alors nous avons construit le reste. Aujourd’hui, plus de 100 agences en Belgique et en France travaillent avec nous, et le CRM reste ouvert de leur arrivée jusqu’à la fermeture.' },
          { t: 'h2', v: 'Le poste' },
          { t: 'p', v: 'Vous construisez les écrans où les agents travaillent : le pipeline, l’assistant d’estimation, le générateur de rapports et la boîte qui rassemble e-mails, WhatsApp et appels dans un même fil. Vous portez chaque fonctionnalité de l’interface à l’API et au modèle de données, et vous continuez à la porter après la mise en ligne. Une grande partie de la plateforme existe déjà, donc vous construirez dessus aussi souvent que vous y ajouterez.' },
          { t: 'h2', v: 'Vos missions' },
          {
            t: 'list',
            v: [
              'Livrer des fonctionnalités de bout en bout : interface, route API, modèle de données, et la migration qui va avec.',
              'Travailler sur le générateur de rapports, qui transforme un template en rapport d’estimation PDF aux couleurs de chaque agence. C’est la partie du produit que nos clients remettent à leurs propres clients.',
              'Garder l’application rapide. Elle reste ouverte toute la journée, et un pipeline porte des milliers de leads par agence.',
              'Maintenir et améliorer du code que vous n’avez pas écrit.',
            ],
          },
          { t: 'h2', v: 'Votre profil' },
          {
            t: 'group',
            label: '📈 Expérience & parcours',
            v: [
              'Au moins un an d’expérience à livrer du logiciel à de vrais utilisateurs.',
              'Vous avez suivi une fonctionnalité après sa mise en ligne : bugs, retours et itération suivante.',
              'Un diplôme en informatique ou en ingénierie, ou des projets qui le démontrent.',
              'Néerlandais langue maternelle.',
            ],
          },
          {
            t: 'group',
            label: '💻 Compétences techniques',
            v: [
              'TypeScript et React. Une expérience avec le App Router de Next.js et les server components est un vrai plus.',
              'À l’aise avec une base NoSQL et avec l’exploitation d’un service sur AWS.',
              'Le sens du détail d’interface. Une grande partie de ce produit, ce sont des tableaux et des formulaires denses utilisés pendant des heures.',
              'Une expérience des API LLM est un plus. Il y a autant de travail sur l’IA que vous en voulez.',
            ],
          },
          {
            t: 'group',
            label: '🧠 État d’esprit',
            v: [
              'Vous préférez livrer quelque chose de petit et l’améliorer plutôt qu’attendre que ce soit parfait.',
              'Vous assumez ce que vous construisez une fois en ligne.',
              'Vous regardez ce que le code fait déjà avant d’y ajouter.',
              'Vous voulez comprendre comment les agents travaillent vraiment, et vous le leur demandez.',
            ],
          },
          { t: 'h2', v: 'Ce que nous offrons' },
          {
            t: 'list',
            v: [
              'Un package salarial compétitif.',
              'Une petite équipe. Ce que vous livrez est chez les agences en quelques jours, et vous décidez de son allure et de son comportement.',
              'Un bureau chez [WAT](https://www.linkedin.com/company/wat-bxl), le meilleur incubateur de Bruxelles, aux côtés d’autres jeunes entreprises tech.',
              'Un produit utilisé chaque jour, avec des clients qui vous disent ce dont ils ont besoin.',
            ],
          },
          { t: 'h2', v: 'Notre processus' },
          {
            t: 'list',
            v: [
              'Un premier entretien en ligne avec le CTO de Cultiv (30 min).',
              'L’entretien technique dans nos bureaux.',
              'Vous rencontrez le reste de l’équipe autour d’un déjeuner.',
            ],
          },
        ],
      },
    },
  },
];
